/**
 * FeatureGate -- wrapper UI que decide entre renderizar la vista premium o un
 * banner de upgrade segun el resultado de useFeatureGate.
 *
 * Uso tipico al inicio de una `renderXxx(container)`:
 *
 *   import { renderFeatureGate } from '../components/FeatureGate.js';
 *   import { useFeatureGate } from '../hooks/useFeatureGate.js';
 *
 *   const gate = await useFeatureGate(businessId, 'publicidad');
 *   if (gate.blocked) {
 *     renderFeatureGate(container, gate, { feature: 'publicidad', viewName: 'Publicidad' });
 *     return;
 *   }
 *   // ... render normal de la vista
 *
 * @module presentation/components/FeatureGate
 */

const REASON_TITLE = {
  no_business:         'Selecciona un negocio',
  no_subscription:     'Sin suscripcion activa',
  feature_disabled:    'Feature no incluida en tu plan',
  feature_not_in_plan: 'Feature no catalogada',
  error:               'No se pudo verificar tu plan',
  ok:                  'Acceso permitido' // no usado en blocked path
};

const REASON_HELP = {
  no_business:         'Elige un negocio en la barra superior para continuar.',
  no_subscription:     'Renueva tu suscripcion para acceder a esta vista.',
  feature_disabled:    'Actualiza tu plan para desbloquear esta funcion.',
  feature_not_in_plan: 'Esta funcion requiere un plan superior.',
  error:               'Reintenta en unos segundos. Si persiste, contacta soporte.'
};

/**
 * Renderiza el banner de upgrade dentro de `container`.
 * @param {HTMLElement} container
 * @param {import('../hooks/useFeatureGate.js').FeatureGateResult} gate
 * @param {{feature:string, viewName:string}} options
 */
export function renderFeatureGate(container, gate, options = {}) {
  if (!container) return;

  const reason = gate?.reason || 'error';
  const title = REASON_TITLE[reason] || REASON_TITLE.error;
  const help = REASON_HELP[reason] || REASON_HELP.error;
  const viewName = options.viewName || 'Esta vista';
  const feature = options.feature || '';
  const message = gate?.message || '';
  const planCode = gate?.planCode || null;
  const days = (typeof gate?.daysToExpiry === 'number') ? gate.daysToExpiry : null;

  const expiryHint = days != null && days < 0
    ? `<p class="fg-expiry">⚠ Tu suscripcion vencio hace ${Math.abs(days)} dias.</p>`
    : (days != null && days <= 7
        ? `<p class="fg-expiry">⏳ Tu suscripcion vence en ${days} dias.</p>`
        : '');

  const planChip = planCode
    ? `<span class="fg-plan">Plan actual: <strong>${escapeHtml(planCode)}</strong></span>`
    : '';

  container.innerHTML = `
    <section class="feature-gate" role="alert" aria-live="polite">
      <div class="fg-card">
        <div class="fg-icon">🔒</div>
        <h2 class="fg-title">${escapeHtml(title)}</h2>
        <p class="fg-view">${escapeHtml(viewName)}${feature ? ` &middot; <code>${escapeHtml(feature)}</code>` : ''}</p>
        ${message ? `<p class="fg-message">${escapeHtml(message)}</p>` : ''}
        ${expiryHint}
        ${planChip}
        <p class="fg-help">${escapeHtml(help)}</p>
        <div class="fg-actions">
          <a href="#/businesses" class="btn-primary">Ver suscripcion</a>
          <a href="#/" class="btn-secondary">Volver al dashboard</a>
        </div>
      </div>
    </section>
    <style>
      .feature-gate {
        display: flex; align-items: center; justify-content: center;
        min-height: 60vh; padding: 24px;
      }
      .feature-gate .fg-card {
        background: #161b22; border: 1px solid #30363d; border-radius: 12px;
        padding: 32px; max-width: 480px; text-align: center; color: #c9d1d9;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      }
      .feature-gate .fg-icon { font-size: 48px; margin-bottom: 12px; }
      .feature-gate .fg-title { margin: 8px 0; color: #f0f6fc; font-size: 1.4rem; }
      .feature-gate .fg-view { color: #8b949e; font-size: 0.875rem; margin: 4px 0 16px; }
      .feature-gate .fg-view code {
        background: #21262d; padding: 2px 6px; border-radius: 4px;
        color: #79c0ff; font-size: 0.875rem;
      }
      .feature-gate .fg-message { color: #c9d1d9; margin: 12px 0; }
      .feature-gate .fg-expiry {
        background: #2d2415; border: 1px solid #d29922; color: #f0883e;
        padding: 8px 12px; border-radius: 6px; margin: 12px 0; font-size: 0.875rem;
      }
      .feature-gate .fg-plan {
        display: inline-block; background: #21262d; padding: 4px 12px;
        border-radius: 12px; font-size: 0.875rem; color: #8b949e; margin: 8px 0;
      }
      .feature-gate .fg-plan strong { color: #79c0ff; text-transform: capitalize; }
      .feature-gate .fg-help { color: #8b949e; margin: 16px 0 24px; font-size: 0.88rem; }
      .feature-gate .fg-actions {
        display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;
      }
      .feature-gate .btn-primary {
        background: #238636; color: #fff; padding: 8px 16px; border-radius: 6px;
        text-decoration: none; font-size: 0.88rem;
      }
      .feature-gate .btn-primary:hover { background: #2ea043; }
      .feature-gate .btn-secondary {
        background: transparent; color: #58a6ff; padding: 8px 16px;
        border: 1px solid #30363d; border-radius: 6px;
        text-decoration: none; font-size: 0.88rem;
      }
      .feature-gate .btn-secondary:hover { background: #21262d; }
    </style>
  `;
}

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escapeHtml } from '../shared/texto.js';
